// ******************************************************************************************
// Copyright (c) 2019-2026 Markadoc Corp and Ontario Partners LLC of NY. All Rights Reserved
// ******************************************************************************************

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { tap } from "rxjs/operators";
import moment from "moment";
import { environment } from "../../environments/environment";
import { UserInfo } from "../user-acct/model/userinfo.model";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isValid: any;

  constructor(private http: HttpClient) {}

  login(user: string, password: string) {
    return this.http
      .post<{ token: string; reset?: string; signup?: string }>(
        environment.apiUrl + "/auth", 
        { user, password }
      )
      .pipe(tap((res) => this.setSession(res)));
  }

  reset(user: string, password: string) {
    return this.http
      .post<{ token: string; reset?: string; signup?: string }>(
        environment.apiUrl + "/auth/reset", 
        { user, password }
      )
      .pipe(tap((res) => this.setSession(res)));
  }

  setSession(authResult: { token: string; reset?: string; signup?: string }) {
    if (!authResult || !authResult.token) {
      return;
    }

    const token = this._decodeToken(authResult.token);
    if (!token) {
      console.error("Failed to decode auth token");
      return;
    }

    if (authResult.reset) {
      localStorage.setItem("reset", authResult.reset);
    }
    if (authResult.signup) {
      localStorage.setItem("signup", authResult.signup);
    }

    localStorage.setItem("id_token", authResult.token);
    if (token.exp) {
      localStorage.setItem("expires_at", JSON.stringify(token.exp * 1000));
    }
  }

  private _decodeToken(token: any): any {
    if (!token) {
      return null;
    }

    // Defensive handling: ensure token is a string and strip accidental JSON wrapping quotes
    let tokenStr = token;
    if (typeof tokenStr !== 'string') {
      try {
        tokenStr = String(tokenStr);
      } catch (e) {
        return null;
      }
    }

    tokenStr = tokenStr.trim();
    if (tokenStr.startsWith('"') && tokenStr.endsWith('"')) {
      tokenStr = tokenStr.slice(1, -1);
    }

    if (typeof tokenStr !== 'string' || !tokenStr.includes('.')) {
      return null;
    }

    try {
      const parts = tokenStr.split(".");
      if (parts.length !== 3) {
        return null;
      }

      let base64Url = parts[1];
      let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      
      // Pad base64 string if necessary
      while (base64.length % 4 !== 0) {
        base64 += "=";
      }

      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error("Token parsing error:", e);
      return null;
    }
  }

  getReset() {
    return localStorage.getItem("reset");
  }

  clearReset() {
    localStorage.removeItem("reset");
  }

  logout() {
    this.updateUserLogoutInfo();
    localStorage.removeItem("reset");
    localStorage.removeItem("id_token");
    localStorage.removeItem("expires_at");
  }

  public isLoggedIn() {
    const expiration = this.getExpiration();
    return expiration ? moment().isBefore(expiration) : false;
  }

  isLoggedOut() {
    return !this.isLoggedIn();
  }

  getExpiration() {
    const expiration = localStorage.getItem("expires_at");
    if (!expiration) return null;
    try {
      const expiresAt = JSON.parse(expiration);
      return moment(expiresAt);
    } catch (e) {
      return null;
    }
  }

  getInfo() {
    const tokenInfo = this.getTokenInfo();
    const uid = tokenInfo ? tokenInfo.uid : "";
    return this.http.get<UserInfo>(environment.apiUrl + "/user/" + uid);
  }

  getTokenInfo() {
    return this._decodeToken(this.getToken());
  }

  async validateToken() {
    let valid: any;
    try {
      valid = await this.http.get(environment.apiUrl + "/status").toPromise();
    } catch (e) {
      console.log("ERROR: " + e);
    }
    return !!(valid && "status" in valid && valid.status === "ok");
  }

  getToken() {
    return localStorage.getItem("id_token");
  }

  updateUserLogoutInfo() {
    const now = new Date().toISOString().substring(0, 23);
    const token = this.getTokenInfo();
    if (!token || !token.uid) {
      return;
    }

    const uid = token.uid;
    const lodata = { last_logout: now };

    return this.http
      .put(environment.apiUrl + "/user/" + uid, JSON.stringify(lodata))
      .subscribe({
        next: (x) => {
          console.log("auth logout at", now);
          return x;
        },
        error: (e) => {
          console.error("auth failed to edit user: ", uid, " error:", e);
        }
      });
  }
}